/**
 * Every rebindable key, by what it does. Values are KeyboardEvent.code
 * strings; an action fires on any of its keys. The settings panel edits
 * these, and Input reads them.
 */
export type Action =
  | "left"
  | "right"
  | "up"
  | "down"
  | "brace"
  | "throw"
  | "turnLeft"
  | "turnRight"
  | "inventory"
  | "pack"
  | "crafting"
  | "camera"
  | "mute"
  | "respawn"
  | "undo"
  | "rotate"
  | "eat"
  | "photo"
  | "settings";

export const DEFAULT_KEYS: Record<Action, string[]> = {
  left: ["KeyA", "ArrowLeft"],
  right: ["KeyD", "ArrowRight"],
  up: ["KeyW", "ArrowUp"],
  down: ["KeyS", "ArrowDown"],
  brace: ["Space"],
  throw: ["KeyG"],
  turnLeft: ["KeyQ"],
  turnRight: ["KeyE"],
  inventory: ["KeyI"],
  pack: ["Tab"],
  crafting: ["KeyC"],
  camera: ["KeyV"],
  mute: ["KeyM"],
  respawn: ["KeyR"],
  undo: ["KeyZ"],
  rotate: ["KeyT"],
  eat: ["KeyH"],
  photo: ["KeyP"],
  settings: ["KeyO"],
};

/** What each action is called in the settings panel. */
export const ACTION_NAMES: Record<Action, string> = {
  left: "Walk left / back up",
  right: "Walk right / go forward",
  up: "Crawl or push up the screen; climb, rise",
  down: "Crawl or push down the screen; climb down, sink",
  brace: "Brace against the rock you hold; let go to push off toward the aim",
  throw: "Throw (or just put down) what's in your hand toward the aim: it hangs there for anyone to pick up, and you go the other way",
  turnLeft: "Turn counter-clockwise (the ship at the helm; yourself in open space)",
  turnRight: "Turn clockwise (the ship at the helm; yourself in open space)",
  inventory: "Inventory and crafting",
  pack: "Pack: the slot grid and hotbar",
  crafting: "Crafting",
  camera: "Camera turns / world-up",
  mute: "Sound on / off",
  respawn: "Start over (your bed, or a rock at the start)",
  undo: "Undo last block (with Ctrl)",
  rotate: "Rotate what you're placing",
  eat: "Eat (heal)",
  photo: "Photo mode",
  settings: "Settings",
};

/** A key code as a short label: "KeyA" -> "A", "ArrowLeft" -> "←". */
export function keyLabel(code: string): string {
  const arrows: Record<string, string> = { ArrowLeft: "←", ArrowRight: "→", ArrowUp: "↑", ArrowDown: "↓" };
  if (arrows[code]) return arrows[code]!;
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  return code.replace(/Left$|Right$/, "");
}
